/**
 * The six series of GET /admin-api/metrics, turned into chart input.
 *
 * ROW SHAPES — transcribed from adminMetrics.ts:128-138, whose comment says
 * "keep this table in step with the SELECT lists". If a chart goes blank, diff
 * these six lines against that comment FIRST:
 *   sheetsCallsPerMinute  { minute, calls }
 *   cacheHitRate          { outcome, events }              outcome: hit|miss
 *   ebayCallsPerDay       { day, calls }
 *   scanLatency           { route, p50_ms, p95_ms }        route: product|stock_batch
 *   jobRuns               { type, outcome, runs, p50_duration_ms }
 *   offlineQueueDepth     { day, max_depth, samples }
 *
 * `samples` on offlineQueueDepth is a real column and is rendered as a
 * confidence hint, not dropped: max() is not weighted by _sample_interval (a
 * max is not an estimator over a sample count), so a peak backed by two
 * samples deserves less trust than one backed by two thousand.
 *
 * A series is `null` when its query failed; the bundle's `partial` flag says
 * at least one did. null is NOT an empty array: "we could not ask" and "the
 * answer is nothing" are different statements and get different copy.
 *
 * ── WHY EVERY READER RETURNS `dropped` (deviation from the brief's plain
 *    array return, and the reason is this task's whole risk class) ───────────
 * The AE SQL API's numeric columns arrive as strings in some shapes, so every
 * reader coerces. A coercion that cannot be made has exactly two honest
 * outcomes and one dishonest one: drop the row and SAY SO, or refuse the
 * series. Silently substituting 0 -- which `Number(null)`, `Number('')` and
 * `Number([])` all hand you -- paints a trough that never happened, and a
 * trough in "Maximale Offline-Warteschlange" reads as "the queue drained".
 * So a row that cannot be read is dropped and COUNTED, and the panel says how
 * many. A chart drawn from a partially-readable series must not look complete,
 * for the same reason a partial bundle must not.
 *
 * PURE, AND DELIBERATELY SO: a shape change on the API side is then a red unit
 * test here rather than a blank chart in production.
 */
import { t } from './i18n';
import type { MetricSeriesName, MetricsBundle } from './types';

// ── coercion ─────────────────────────────────────────────────────────────────

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A finite number, or a non-empty numeric string. Everything else is `null` --
 * NOT 0. `Number('')`, `Number(null)`, `Number([])` and `Number(false)` are all
 * 0, which is why the emptiness check comes first and why booleans and objects
 * are refused outright rather than handed to `Number`.
 */
export function readNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** A non-empty string label. A number is NOT accepted: every label column in
 *  the six shapes is a blob or a time bucket, and accepting a number here
 *  would let a mis-selected column pass as an axis tick. */
export function readLabel(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** What every array reader answers: the rows it could read, and how many it
 *  could not. `dropped > 0` is rendered, never swallowed. */
export interface Series<P> { points: P[]; dropped: number }

function readRows<P>(rows: unknown[], read: (row: Record<string, unknown>) => P | null): Series<P> {
  const points: P[] = [];
  let dropped = 0;
  for (const row of rows) {
    const point = isRecord(row) ? read(row) : null;
    if (point === null) dropped += 1;
    else points.push(point);
  }
  return { points, dropped };
}

// ── the six readers ──────────────────────────────────────────────────────────

export interface TimePoint { at: string; value: number }

/** `{ minute, calls }` — read verbs only (`blob2 IN ('get','get_meta')`). See
 *  `seriesLabel` for why that scope is part of the LABEL and not a footnote. */
export function sheetsPerMinute(rows: unknown[]): Series<TimePoint> {
  return readRows(rows, (row) => {
    const at = readLabel(row.minute);
    const value = readNumber(row.calls);
    return at === null || value === null ? null : { at, value };
  });
}

export interface CacheRatio { hit: number; miss: number; ratio: number | null; dropped: number }

/**
 * `{ outcome, events }`, outcome `hit|miss`.
 *
 * `ratio` is `null` when there were no events at all: a hit rate over zero
 * events is not 0 %, it is UNKNOWN, and "0 %" over an idle hour would send an
 * operator looking for a cache that is working fine. An outcome outside the
 * two is DROPPED rather than folded into `miss` -- a third outcome nobody
 * planned for must not silently depress the rate.
 */
export function cacheHitRatio(rows: unknown[]): CacheRatio {
  let hit = 0;
  let miss = 0;
  let dropped = 0;
  for (const row of rows) {
    const outcome = isRecord(row) ? readLabel(row.outcome) : null;
    const events = isRecord(row) ? readNumber(row.events) : null;
    if (events === null || (outcome !== 'hit' && outcome !== 'miss')) {
      dropped += 1;
      continue;
    }
    if (outcome === 'hit') hit += events;
    else miss += events;
  }
  const total = hit + miss;
  return { hit, miss, ratio: total === 0 ? null : hit / total, dropped };
}

/** `{ day, calls }`. */
export function ebayPerDay(rows: unknown[]): Series<TimePoint> {
  return readRows(rows, (row) => {
    const at = readLabel(row.day);
    const value = readNumber(row.calls);
    return at === null || value === null ? null : { at, value };
  });
}

export interface LatencyRow { route: string; p50Ms: number; p95Ms: number }

/**
 * `{ route, p50_ms, p95_ms }`, route `product|stock_batch`.
 *
 * An unrecognised route is KEPT and rendered verbatim (`routeLabel` below),
 * the same discipline lib/ebayView.ts's `ebayScopeLabel` follows: the server's
 * WHERE clause already restricts this to the two, so a third value means the
 * registry moved, and hiding it would hide the move.
 */
export function latencyByRoute(rows: unknown[]): Series<LatencyRow> {
  return readRows(rows, (row) => {
    const route = readLabel(row.route);
    const p50Ms = readNumber(row.p50_ms);
    const p95Ms = readNumber(row.p95_ms);
    return route === null || p50Ms === null || p95Ms === null ? null : { route, p50Ms, p95Ms };
  });
}

export interface JobRunRow {
  type: string;
  runs: number;
  errors: number;
  /** `null` when `runs` is 0 -- an error rate over no runs is unknown, not 0 %. */
  errorRate: number | null;
  /**
   * The p50 of the SUCCESSFUL runs, and `null` when this type has none in the
   * window. The SELECT groups by (type, outcome), so each type arrives as up to
   * TWO medians -- and two medians cannot be combined into one. A runs-weighted
   * mean of them would be a number nobody measured, so the column reports the
   * one it can name and the header says which (`jobs.metrics.jobRuns.p50`).
   */
  p50DurationMs: number | null;
}

/**
 * `{ type, outcome, runs, p50_duration_ms }`, outcome `ok|error`.
 *
 * An outcome outside the two is DROPPED, never counted as a success: an
 * unrecognised third value folded into `runs` alone would LOWER the error rate
 * of a job type that had just started failing in a new way.
 */
export function jobRunSummary(rows: unknown[]): Series<JobRunRow> {
  const byType = new Map<string, { runs: number; errors: number; okP50: number | null }>();
  let dropped = 0;
  for (const row of rows) {
    const type = isRecord(row) ? readLabel(row.type) : null;
    const outcome = isRecord(row) ? readLabel(row.outcome) : null;
    const runs = isRecord(row) ? readNumber(row.runs) : null;
    if (type === null || runs === null || (outcome !== 'ok' && outcome !== 'error')) {
      dropped += 1;
      continue;
    }
    const entry = byType.get(type) ?? { runs: 0, errors: 0, okP50: null };
    entry.runs += runs;
    if (outcome === 'error') entry.errors += runs;
    else entry.okP50 = isRecord(row) ? readNumber(row.p50_duration_ms) : null;
    byType.set(type, entry);
  }
  const points = [...byType.entries()]
    .map(([type, e]) => ({
      type,
      runs: e.runs,
      errors: e.errors,
      errorRate: e.runs === 0 ? null : e.errors / e.runs,
      p50DurationMs: e.okP50,
    }))
    .sort((a, b) => a.type.localeCompare(b.type));
  return { points, dropped };
}

export interface QueueDepthPoint extends TimePoint { samples: number }

/** `{ day, max_depth, samples }` — `samples` is the confidence hint, see the
 *  module docblock for why a max is not sample-weighted. */
export function queueDepthPerDay(rows: unknown[]): Series<QueueDepthPoint> {
  return readRows(rows, (row) => {
    const at = readLabel(row.day);
    const value = readNumber(row.max_depth);
    const samples = readNumber(row.samples);
    return at === null || value === null || samples === null ? null : { at, value, samples };
  });
}

// ── formatting ───────────────────────────────────────────────────────────────

const COUNT_FORMAT = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
const PERCENT_FORMAT = new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 1 });

export function formatCount(value: number): string {
  return Number.isFinite(value) ? COUNT_FORMAT.format(value) : t('jobs.metrics.unknown');
}

/** `null` is "unbekannt", never "0 %". The distinction is the whole reason
 *  `cacheHitRatio` and `jobRunSummary` return a nullable ratio at all. */
export function formatRatio(ratio: number | null): string {
  return ratio === null ? t('jobs.metrics.unknown') : PERCENT_FORMAT.format(ratio);
}

export function formatMs(ms: number | null): string {
  return ms === null ? t('jobs.metrics.unknown') : t('jobs.metrics.ms', { ms: COUNT_FORMAT.format(ms) });
}

/**
 * An AE time bucket (`toStartOfInterval` answers `2026-08-24 13:00:00`, a SPACE
 * rather than a `T`) as an axis tick. Unparseable values are shown verbatim
 * rather than as "Invalid Date" -- the same rule `formatTimestamp` follows.
 */
const TICK_FORMAT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' });
const DAY_FORMAT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' });

export function formatBucket(bucket: string, granularity: 'minute' | 'day'): string {
  const ms = Date.parse(bucket.includes('T') ? bucket : bucket.replace(' ', 'T'));
  if (!Number.isFinite(ms)) return bucket;
  return (granularity === 'day' ? DAY_FORMAT : TICK_FORMAT).format(new Date(ms));
}

// ── the labelling rule ───────────────────────────────────────────────────────

/**
 * THE ONE LABEL THAT IS A CORRECTNESS QUESTION, not a copy question.
 *
 * `sheetsCallsPerMinute` counts READ VERBS ONLY (`blob2 IN ('get','get_meta')`),
 * while phase 5a's fairness budget -- `SHEETS_CALLS_PER_TENANT_PER_DAY = 10000`
 * in api/src/jobs/budget.ts -- counts EVERY Sheets verb (token, update, append,
 * batch_update, values_batch_update, clear all increment
 * `metrics.sheetsCallCount` too). The two numbers can therefore never be
 * reconciled, and the merged source flags exactly this as a 3c LABELLING hazard
 * (api/src/adminMetrics.ts, marker M-5: "3c must not label this chart with the
 * budget's name").
 *
 * So: never "Kontingent", never "Budget", never "Limit" -- on ANY of the six,
 * because the ban is on claiming a chart is the thing a tenant gets throttled
 * by, and only one of these six even resembles it. `FORBIDDEN_LABEL_WORDS` is
 * asserted over the whole table in test/metrics.test.ts, so the ban is a
 * property of the table rather than a note beside one row.
 */
export const FORBIDDEN_LABEL_WORDS = ['Kontingent', 'Budget', 'Limit'] as const;

export const METRIC_SERIES: readonly MetricSeriesName[] = [
  'sheetsCallsPerMinute', 'cacheHitRate', 'ebayCallsPerDay',
  'scanLatency', 'jobRuns', 'offlineQueueDepth',
] as const;

/**
 * One series out of a bundle, or `null` for "we could not ask".
 *
 * ANYTHING THAT IS NOT AN ARRAY IS `null`, not `[]`. The server sends `null`
 * for a failed query, and api.ts's unchecked `body as T` cast means a missing
 * or malformed `series` object is reachable too -- all of which are "we could
 * not ask", and none of which is "the answer is nothing". Collapsing them into
 * an empty array is how a broken query becomes a chart that says zero.
 */
export function seriesRowsOf(bundle: MetricsBundle, name: MetricSeriesName): unknown[] | null {
  const rows = (bundle.series as Partial<Record<MetricSeriesName, unknown>> | undefined)?.[name];
  return Array.isArray(rows) ? rows : null;
}

/** Literal `t()` calls, one per series, for the reason Blocking.tsx's `copyFor`
 *  gives: a computed key still renders, but test/i18nKeys.test.ts could no
 *  longer prove the copy exists. */
export function seriesLabel(series: MetricSeriesName): string {
  switch (series) {
    case 'sheetsCallsPerMinute': return t('jobs.metrics.sheetsCallsPerMinute.label');
    case 'cacheHitRate': return t('jobs.metrics.cacheHitRate.label');
    case 'ebayCallsPerDay': return t('jobs.metrics.ebayCallsPerDay.label');
    case 'scanLatency': return t('jobs.metrics.scanLatency.label');
    case 'jobRuns': return t('jobs.metrics.jobRuns.label');
    case 'offlineQueueDepth': return t('jobs.metrics.offlineQueueDepth.label');
  }
}

/** The scope or the caveat that belongs WITH the label, not in a footnote a
 *  reader can miss -- four of the six carry one. */
export function seriesNote(series: MetricSeriesName): string | null {
  switch (series) {
    case 'sheetsCallsPerMinute': return t('jobs.metrics.sheetsCallsPerMinute.note');
    case 'cacheHitRate': return null;
    case 'ebayCallsPerDay': return null;
    case 'scanLatency': return t('jobs.metrics.scanLatency.note');
    case 'jobRuns': return t('jobs.metrics.jobRuns.note');
    case 'offlineQueueDepth': return t('jobs.metrics.offlineQueueDepth.note');
  }
}

/** `product` / `stock_batch` in German; anything else verbatim, for the reason
 *  `latencyByRoute` keeps it. */
export function routeLabel(route: string): string {
  switch (route) {
    case 'product': return t('jobs.metrics.scanLatency.route.product');
    case 'stock_batch': return t('jobs.metrics.scanLatency.route.stockBatch');
    default: return route;
  }
}

// ── the render plan: which number goes in which column ───────────────────────

/**
 * WHICH NUMBER GOES WHERE, decided HERE (Task 13 review M1).
 *
 * The first round left this mapping as ~115 lines of JSX inside
 * `views/Jobs.tsx`'s `SeriesRows`, and eight independently-chosen mutations
 * survived the whole suite there -- among them a jobs table reporting a
 * 100 %-failing type as "0 % Fehlerquote", the error COUNT rendered under the
 * "p50 (erfolgreiche Läufe)" header, p50 and p95 swapped, and a ratio bar whose
 * counts contradicted its own percentage. Every one of those is a
 * "which field lands in which cell" decision, which is exactly the kind of
 * decision this package keeps out of renderers.
 *
 * So the mapping is a TABLE KEYED BY THE SERIES UNION (`SERIES_PLAN` below), in
 * the same construction the six `stats_json` shapes use: a seventh series is a
 * compile error here and in test/metrics.test.ts's expected-plan table, and the
 * eight mutations above now have a Node target.
 *
 * EVERY RENDERED STRING IS IN THE PLAN -- the axis maximum, each point's native
 * `<title>`, the ratio's percentage and counts lines, every table cell. The
 * chart components receive the plan and render it; they format nothing. That is
 * what makes `<RatioBar hit={miss} miss={hit}>` unexpressible rather than
 * merely untested.
 */
export interface PlanPoint {
  /** Axis tick / hover label, already formatted. */
  label: string;
  /** The number the geometry uses. Always finite. */
  value: number;
  /** The native `<title>` on this point, already assembled. */
  title: string;
}

export interface PlanRow { key: string; cells: string[] }

interface PlanBase { dropped: number; count: number }

export interface LinePlan extends PlanBase { kind: 'line'; points: PlanPoint[]; ariaLabel: string; maxLabel: string }
export interface BarPlan extends PlanBase { kind: 'bar'; points: PlanPoint[]; ariaLabel: string; maxLabel: string }
export interface RatioPlan extends PlanBase {
  kind: 'ratio';
  /** `null` = unknown; the bar is not drawn at all. */
  ratio: number | null;
  valueLine: string;
  countsLine: string;
  unknownLine: string;
  ariaLabel: string;
}
export interface TablePlan extends PlanBase { kind: 'table'; columns: string[]; rows: PlanRow[] }

export type SeriesPlan = LinePlan | BarPlan | RatioPlan | TablePlan;

const peakOf = (points: readonly { value: number }[]): number =>
  points.reduce((max, p) => (p.value > max ? p.value : max), 0);

function pointTitle(label: string, value: number, unit: string, hint?: string): string {
  return `${label}: ${formatCount(value)} ${unit}${hint === undefined ? '' : ` (${hint})`}`;
}

/**
 * One builder per series. `Record<MetricSeriesName, …>`, so the table cannot
 * gain a series without gaining a builder.
 */
const SERIES_PLAN: Record<MetricSeriesName, (rows: unknown[]) => SeriesPlan> = {
  sheetsCallsPerMinute: (rows) => {
    const series = sheetsPerMinute(rows);
    const unit = t('jobs.metrics.unit.calls');
    return {
      kind: 'line',
      points: series.points.map((p) => {
        const label = formatBucket(p.at, 'minute');
        return { label, value: p.value, title: pointTitle(label, p.value, unit) };
      }),
      ariaLabel: t('jobs.metrics.sheetsCallsPerMinute.aria', {
        points: series.points.length, peak: formatCount(peakOf(series.points)),
      }),
      maxLabel: `${formatCount(Math.max(peakOf(series.points), 1))} ${unit}`,
      dropped: series.dropped,
      count: series.points.length,
    };
  },

  cacheHitRate: (rows) => {
    const ratio = cacheHitRatio(rows);
    return {
      kind: 'ratio',
      ratio: ratio.ratio,
      valueLine: formatRatio(ratio.ratio),
      // The ABSOLUTE counts, beside the bar: 100 % over four events and 100 %
      // over four hundred thousand are the same bar and different facts.
      countsLine: t('jobs.metrics.cacheHitRate.counts', {
        hit: formatCount(ratio.hit), miss: formatCount(ratio.miss),
      }),
      unknownLine: t('jobs.metrics.cacheHitRate.unknown'),
      ariaLabel: t('jobs.metrics.cacheHitRate.aria', { ratio: formatRatio(ratio.ratio) }),
      dropped: ratio.dropped,
      count: ratio.hit + ratio.miss,
    };
  },

  ebayCallsPerDay: (rows) => {
    const series = ebayPerDay(rows);
    const unit = t('jobs.metrics.unit.calls');
    return {
      kind: 'bar',
      points: series.points.map((p) => {
        // 'day', not 'minute': these buckets are whole days and a time-of-day
        // tick on them is a lie about the granularity.
        const label = formatBucket(p.at, 'day');
        return { label, value: p.value, title: pointTitle(label, p.value, unit) };
      }),
      ariaLabel: t('jobs.metrics.ebayCallsPerDay.aria', {
        points: series.points.length, peak: formatCount(peakOf(series.points)),
      }),
      maxLabel: `${formatCount(Math.max(peakOf(series.points), 1))} ${unit}`,
      dropped: series.dropped,
      count: series.points.length,
    };
  },

  scanLatency: (rows) => {
    const series = latencyByRoute(rows);
    return {
      kind: 'table',
      columns: [
        t('jobs.metrics.scanLatency.route'),
        t('jobs.metrics.scanLatency.p50'),
        t('jobs.metrics.scanLatency.p95'),
      ],
      rows: series.points.map((row) => ({
        key: row.route,
        cells: [routeLabel(row.route), formatMs(row.p50Ms), formatMs(row.p95Ms)],
      })),
      dropped: series.dropped,
      count: series.points.length,
    };
  },

  jobRuns: (rows) => {
    const series = jobRunSummary(rows);
    return {
      kind: 'table',
      columns: [
        t('jobs.metrics.jobRuns.type'),
        t('jobs.metrics.jobRuns.runs'),
        t('jobs.metrics.jobRuns.errorRate'),
        t('jobs.metrics.jobRuns.p50'),
      ],
      rows: series.points.map((row) => ({
        key: row.type,
        // The order is the header's order, and the third cell is the ERROR
        // rate -- not its complement, and not the error count.
        cells: [
          row.type,
          formatCount(row.runs),
          formatRatio(row.errorRate),
          formatMs(row.p50DurationMs),
        ],
      })),
      dropped: series.dropped,
      count: series.points.length,
    };
  },

  offlineQueueDepth: (rows) => {
    const series = queueDepthPerDay(rows);
    const unit = t('jobs.metrics.unit.entries');
    return {
      kind: 'line',
      points: series.points.map((p) => {
        const label = formatBucket(p.at, 'day');
        return {
          label,
          value: p.value,
          // `samples` is the CONFIDENCE hint, never the value: a peak backed by
          // two samples deserves less trust than one backed by two thousand.
          title: pointTitle(label, p.value, unit,
            t('jobs.metrics.offlineQueueDepth.samples', { samples: formatCount(p.samples) })),
        };
      }),
      ariaLabel: t('jobs.metrics.offlineQueueDepth.aria', {
        points: series.points.length, peak: formatCount(peakOf(series.points)),
      }),
      maxLabel: `${formatCount(Math.max(peakOf(series.points), 1))} ${unit}`,
      dropped: series.dropped,
      count: series.points.length,
    };
  },
};

export function seriesPlan(name: MetricSeriesName, rows: unknown[]): SeriesPlan {
  return SERIES_PLAN[name](rows);
}
